import { Component,  OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import {
    ValuationStandard,
    ValuationStandardOnServerCreated,
    selectValuationStandardActionLoading,
    selectValuationStandardById,
    ValuationStandardUpdated,
    selectLastCreatedValuationStandardId
} from 'src/app/core/linked-tables';
import {Location} from '@angular/common';
import {Observable, Subscription, combineLatest} from 'rxjs';

@Component({
    selector: 'kt-valuation-standard-edit',
    templateUrl: './valuation-standards-edit.component.html',
    styleUrls: ['./valuation-standards-edit.component.scss'],
})


export class ValuationStandardEditComponent implements OnInit, OnDestroy {

    model: ValuationStandard;
    oldModel: ValuationStandard;

    modelForm: UntypedFormGroup;

    loadingAfterSubmit = false;
    loading$: Observable<boolean>;
    viewLoading = false;
    componentSubscriptions: Subscription;
    subscriptions: Subscription[] = [];
    mode = 'NEW';

    hasFormErrors = false;
    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService: subheaderService
     * @param activatedRoute: activatedRoute
     * @param router: router
     * @param location: location
     * @param fb: FormBuilder
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private location: Location,
                private fb: UntypedFormBuilder,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectValuationStandardActionLoading));
        const routeSubscription = 
            combineLatest([
                this.activatedRoute.params,
                this.activatedRoute.data
            ]).subscribe(([params, data]) => {
                const id = params.id;
                this.readonly = data.readonly ? true : false 
                if (id && id > 0) {
                    this.store.pipe(select(selectValuationStandardById(id))).subscribe(res => {
                        if (res) {
                            this.oldModel = Object.assign({}, res);
                            this.model = Object.assign({}, this.oldModel);
                            this.initValuationStandard();
                        }
                    });
                } else {
                    this.model = new ValuationStandard();
                    this.model.clear();
                    this.oldModel = Object.assign({}, this.model);
                    this.initValuationStandard();
                }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Valuation Standardss', page: `../default/admin-management/linked-tables/valuation-standards`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ?
                    `../default/admin-management/linked-tables/valuation-standards/edit/${id}` :
                    `../default/admin-management/linked-tables/valuation-standards/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initValuationStandard() {
        this.createForm();
    }


    createForm() {
        this.modelForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('VALUATION_STANDARD.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('VALUATION_STANDARD.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.modelForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.modelForm.markAsPristine();
        this.modelForm.markAsUntouched();
        this.modelForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.modelForm.controls;
        /** check form */
        if (this.modelForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateValuationStandard(this.model);
        } else {
            this.createValuationStandard(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: ValuationStandardModel
     */
    updateValuationStandard(_model: ValuationStandard) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateValuationStandard: Update<ValuationStandard> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new ValuationStandardUpdated({
            partialValuationStandard: updateValuationStandard,
            valuationStandard: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: ValuationStandardModel
     */
    createValuationStandard(_model: ValuationStandard) {
        this.store.dispatch(new ValuationStandardOnServerCreated({valuationStandard: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedValuationStandardId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
